import CardCommon from '@/pages/opinion/components/CardCommon';
import React, { useMemo, useState } from 'react';
import { BlockChart, TopicItem, PieChart } from './components';
import { supplementSingleData } from '@/pages/opinion/utils';
import { connect, useHistory, useIntl } from 'umi';
import BubbleCommon, {
  overviewTopicTips,
} from '@/pages/opinion/components/BubbleCommon';
import {
  barIcon,
  blockIcon,
  lineIcon,
  pieIcon,
  SymbolType,
  tableIcon,
} from '@/pages/opinion/constants/chart';
import useOverviewTopicTimeline from '../../hooks/useOverviewTopicTimeline';
import DataTable from '@op/pages/Summary/TopicTracking/TopicChartInfo/TopicTrends/DataTable';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import './TopicCard.scss';
import { DBO_ARR, DBO_REQ } from '@op/utils/format';
import { TRequestBackType } from '@op/utils/requestUtil';

function TopicCard({
  filter,
  gameInfo,
  curFilter,
  translateTopic,
  handleClickInfo,
  hasTitleArrow = true,
  handleTitleOnClick,
  position,
  needToFeeds,
  noTimeline = false,
  openUserCache = false,
  downloadFileName = 'Topic Tracking'
}: any) {
  const { formatMessage, locale } = useIntl();
  const { download, downloading } = useDownloadParams();
  const [chartType, setChartType] = useState<SymbolType>('block');

  const realFilter = useMemo(() => {
    return {
      ...filter,
      ...gameInfo,
      sort_item: gameInfo.entity_type === 'mobile' ? 'dau' : 'mentions',
      sort_model: 'desc',
      statistics_field: 'channel',
      top_n: 15,
      need_topic_translation: true,
      need_others: false,
      sentiment_type: curFilter,
    };
  }, [filter, curFilter]);

  const { topic, loading: overviewTopicLoading } = useOverviewTopicTimeline({
    filter: realFilter,
    openUserCache,
  });

  const {
    overviewTopicData,
    overviewTopicSumData,
    topicBlockData,
    blockTotal,
    tableInfo,
  } = useMemo((): {
    overviewTopicData: TRequestBackType;
    overviewTopicSumData: any[];
    topicBlockData: any[];
    blockTotal: number;
    tableInfo: { header: string[]; data: any[] };
  } => {
    let overviewTopicData: TRequestBackType = {};
    let overviewTopicSumData: Array<any> = [];
    let topicBlockData: Array<any> = [];
    let blockTotal = 0;
    let tableInfo = {
      header: [] as string[],
      data: [] as any[],
    };

    if (topic) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const {
        timeline_of_end_time,
        timeline_of_mentions,
        timeline_of_start_time,
        mentions,
        sum,
      } = topic || {};

      overviewTopicData = DBO_REQ.listKeys(
        {
          timeline: timeline_of_mentions,
          timeKey: 'time',
          valueKey: 'count',
          key: 'name',
        },
        {
          timeline_of_start_time,
          timeline_of_end_time,
          filter,
          needTable: true,
          moreLineTable: true,
        },
      );

      const { seriesObj, tableRecord } = overviewTopicData;
      const result = Object.fromEntries(
        Object.entries(seriesObj || {}).map(([k, v]) => [
          k,
          v.reduce((a, b) => a + b),
        ]),
      );
      overviewTopicData.legend?.sort(
        DBO_ARR.sortByMap(result, (item) => item, 'desc'),
      );

      topicBlockData = mentions || [];
      overviewTopicSumData = sum?.timeline_total;
      blockTotal = sum?.total;
      tableInfo = {
        header: overviewTopicData.legend || [],
        data: tableRecord || [],
      };
    }

    return {
      overviewTopicData,
      overviewTopicSumData,
      topicBlockData,
      blockTotal,
      tableInfo,
    };
  }, [topic, filter]);

  const history = useHistory();

  // 下载处理函数
  const handleDownload = async () => {
    await download({
      key: 'TopicTrackingTrends',
      fileName: `${downloadFileName} Trends-mentions`,
      filter: {
        ...filter,
        ...gameInfo,
      },
      data: tableInfo.data || [],
      auxiliaryParams: {
        tableHeader: (tableInfo.header || []).map((item: string) => {
          const key = item?.toLowerCase();
          const translation = translateTopic?.[key];

          // 处理国际化翻译
          let title = item;
          if (translation && typeof translation === 'object') {
            title =
              translation[locale] ||
              translation['en-US'] ||
              translation['zh-CN'] ||
              item;
          } else if (translation) {
            title = translation;
          }

          return {
            title,
            key: item,
          };
        }),
        indicators: 'mentions',
      },
    });
  };

  return (
    <CardCommon
      loading={overviewTopicLoading}
      empty={overviewTopicData.isEmpty}
      emptyHeight={327}
      loadingHeight={327}
      title={
        <BubbleCommon tipsMap={overviewTopicTips}>
          {formatMessage({ id: 'Top 15 Topics' })}
        </BubbleCommon>
      }
      defaultOperation={chartType}
      operations={
        noTimeline
          ? [blockIcon, tableIcon, pieIcon]
          : [lineIcon, barIcon, blockIcon, tableIcon, pieIcon]
      }
      handleOperationsOnClick={(item) => {
        setChartType(item.symbol as SymbolType);
      }}
      handleTitleOnClick={() => {
        if (hasTitleArrow) {
          if (handleTitleOnClick) {
            handleTitleOnClick();
          } else {
            const sentimentTypeMap: any = {
              emerging: 'total',
            };
            const sentiment = sentimentTypeMap[curFilter] || curFilter;
            history.push('/opinion/Summary/TopicTracking', {
              topicLevel: 'L2',
              chartType: 'block',
              sentimentType: sentiment,
            });
          }
        }
      }}
      contentCard
      hasTitleArrow={hasTitleArrow}
      logBtnId="TopicCard"
      hasDownload={!overviewTopicData.isEmpty}
      downloadLoading={downloading}
      handleDownloadOnClick={handleDownload}
    >
      <>
        {chartType === 'block' && (
          <BlockChart
            formatMessage={formatMessage}
            data={topicBlockData}
            translationData={translateTopic}
            loading={overviewTopicLoading}
            filter={filter}
            height={327}
            total={blockTotal}
            gameInfo={gameInfo}
            needToFeeds={needToFeeds}
            position={position}
            onClick={(params) => {
              handleClickInfo?.(params);
            }}
          ></BlockChart>
        )}
        {(chartType === 'line' || chartType === 'bar') && (
          <TopicItem
            data={overviewTopicData}
            filter={filter}
            type={chartType}
            translationData={translateTopic}
            sumData={overviewTopicSumData}
            formatMessage={formatMessage}
            handleClickInfo={(params: any) => handleClickInfo?.(params)}
          ></TopicItem>
        )}
        {chartType === 'table' && (
          <DataTable
            indicators="mentions"
            tableInfo={tableInfo}
            transTopic={(name: string) => {
              const key = name?.toLocaleLowerCase();
              const translation = translateTopic?.[key];

              // 如果 translation 是对象（国际化对象），根据当前语言返回对应值
              if (translation && typeof translation === 'object') {
                return (
                  translation[locale] ||
                  translation['en-US'] ||
                  translation['zh-CN'] ||
                  name
                );
              }

              // 如果是字符串，直接返回
              return translation || name;
            }}
            dateType={filter.date_type || 'daily'}
            height={265.5}
          />
        )}
        {chartType === 'pie' && (
          <PieChart
            data={topicBlockData}
            translationData={translateTopic}
            formatMessage={formatMessage}
            filter={filter}
            total={blockTotal}
            height={327}
            loading={overviewTopicLoading}
          ></PieChart>
        )}
      </>
    </CardCommon>
  );
}

export default connect((state: any, ownProps: any) => {
  const { home, opinionCommon, loading } = state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: ownProps.gameInfo || opinionCommon.gameInfo,
  };
})(TopicCard);
